import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Calendar, Users, Columns, Briefcase, Inbox, Zap, CreditCard, Settings as SettingsIcon, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { T } from "../../lib/testIds";
import Logo from "../site/Logo";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, tid: T.admin.linkDashboard, end: true },
  { to: "/admin/calendar", label: "Calendar", icon: Calendar, tid: T.admin.linkCalendar },
  { to: "/admin/contacts", label: "Contacts", icon: Users, tid: T.admin.linkContacts },
  { to: "/admin/pipeline", label: "Pipeline", icon: Columns, tid: T.admin.linkPipeline },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase, tid: T.admin.linkJobs },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox, tid: T.admin.linkInbox },
  { to: "/admin/automations", label: "Automations", icon: Zap, tid: T.admin.linkAutomations },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, tid: "admin-nav-payments" },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <aside
        data-testid={T.admin.sidebar}
        className={`fixed md:relative z-30 h-full w-64 bg-[color:var(--ww-navy)] text-white flex-shrink-0 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="h-20 flex items-center px-5 border-b border-white/10">
          <Logo variant="white-on-dark" size="sm" />
        </div>
        <div className="px-5 mt-4">
          <div className="text-[10px] font-subhead uppercase tracking-widest text-[color:var(--ww-gold)]">Admin Console</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 mt-2">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.tid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-subhead uppercase tracking-wider mb-1 transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
          {user?.role === "owner" && (
            <NavLink
              to="/admin/settings"
              data-testid={T.admin.linkSettings}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-subhead uppercase tracking-wider mb-1 transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`
              }
            >
              <SettingsIcon className="h-4 w-4" />
              Settings
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="text-xs text-slate-400 mb-1">Signed in as</div>
          <div className="text-sm font-semibold mb-3">{user?.name} <span className="ml-1 text-[color:var(--ww-gold)] uppercase text-[10px]">{user?.role}</span></div>
          <button data-testid={T.admin.logout} onClick={doLogout} className="w-full flex items-center justify-center gap-2 text-sm font-subhead uppercase tracking-wider py-2 rounded-md bg-white/5 hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden h-14 bg-white border-b border-slate-200 flex items-center px-4">
          <button onClick={() => setOpen((v) => !v)} className="p-2">{open ? <X /> : <Menu />}</button>
          <div className="ml-2 font-display uppercase tracking-wider text-[color:var(--ww-navy)]">Wonder Washers</div>
        </div>
        <main className="flex-1 overflow-auto p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
